import React, { useState, useRef, useCallback, useEffect } from "react";
import { Dialog } from "@headlessui/react";
import { documentService, type CreateDocumentWorkflowOptions } from '../../../services/documentService';
import {
  WorkflowSuggestionModal,
  type WorkflowSuggestionApplyResult,
} from '../../WorkflowSuggestionModal';

interface FileUpload {
  id: string;
  file: File;
  progress: number;
  status: "pending" | "uploading" | "completed" | "error";
  error?: string;
}

interface UploadModalProps {
  isOpen: boolean;
  onClose: () => void;
  selectedPath?: string;
  onDocumentUploaded?: (response: any) => void;
  /** Quando definido, abre o fluxo de escolha de workflow antes do upload. */
  customerId?: string;
  /** Document.Id da pasta de destino (NodeType = folder). */
  folderDocumentId?: string;
}

const MAX_FILES = 10;
const MAX_FILE_SIZE = 40 * 1024 * 1024; // 40MB
const ALLOWED_TYPES = [
  "application/pdf",
  "application/msword",
  "application/vnd.openxmlformats-officedocument.wordprocessingml.document",
  "application/vnd.ms-excel",
  "application/vnd.openxmlformats-officedocument.spreadsheetml.sheet",
  "text/plain",
  "image/jpeg",
  "image/png",
  "image/gif",
  "application/zip",
  "application/x-zip-compressed",
  "application/octet-stream",
  "application/x-rar-compressed",
  "application/x-7z-compressed",
  "application/gzip",
  "application/x-gzip",
  "application/x-bzip2",
  "application/x-tar",
];

function toUploadWorkflowOptions(
  result: WorkflowSuggestionApplyResult,
  fileExtension: string | undefined
): CreateDocumentWorkflowOptions {
  const base = {
    fileExtension: fileExtension || undefined,
    requiresSignature: result.requiresSignature || undefined,
    signers: result.signers,
    documentPlacements: result.documentPlacements,
    signatureExpiresAt: result.signatureExpiresAt ?? undefined,
  };

  if (result.skipWorkflow) {
    return { ...base, skipWorkflow: true };
  }

  return {
    ...base,
    skipWorkflow: false,
    manualWorkflowId: result.manualWorkflowId,
  };
}

export const UploadModal: React.FC<UploadModalProps> = ({
  isOpen,
  onClose,
  selectedPath,
  onDocumentUploaded,
  customerId,
  folderDocumentId,
}) => {
  const [files, setFiles] = useState<FileUpload[]>([]);
  const [isDragging, setIsDragging] = useState(false);
  const [isUploading, setIsUploading] = useState(false);
  const [uploadError, setUploadError] = useState<string | null>(null);
  const [showWorkflowModal, setShowWorkflowModal] = useState(false);
  const fileInputRef = useRef<HTMLInputElement>(null);
  const abortControllerRef = useRef<AbortController | null>(null);

  // Limpar estado quando o modal for fechado
  useEffect(() => {
    if (!isOpen) {
      setFiles([]);
      setIsDragging(false);
      setIsUploading(false);
      setUploadError(null);
      setShowWorkflowModal(false);
      if (abortControllerRef.current) {
        abortControllerRef.current.abort();
        abortControllerRef.current = null;
      }
      // Limpar o input de arquivo também
      if (fileInputRef.current) {
        fileInputRef.current.value = "";
      }
    }
  }, [isOpen]);

  const validateFile = (file: File): string | null => {
    if (file.size > MAX_FILE_SIZE) {
      return `Arquivo muito grande. Máximo permitido: ${MAX_FILE_SIZE / 1024 / 1024}MB`;
    }
    
    // Verificar por tipo MIME
    if (ALLOWED_TYPES.includes(file.type)) {
      return null;
    }
    
    // Verificar por extensão de arquivo para casos onde o tipo MIME não é detectado corretamente
    const fileName = file.name.toLowerCase();
    const allowedExtensions = [
      '.pdf', '.doc', '.docx', '.xls', '.xlsx', '.txt', '.jpg', '.jpeg', '.png', '.gif',
      '.zip', '.rar', '.7z', '.tar', '.gz', '.bz2', '.csv', '.html', '.edoc'
    ];
    
    const hasValidExtension = allowedExtensions.some(ext => fileName.endsWith(ext));
    if (hasValidExtension) {
      return null;
    }
    
    return "Tipo de arquivo não permitido";
  };

  const addFiles = useCallback(
    (newFiles: FileList | File[]) => {
     
      const fileArray = Array.from(newFiles);
      const validFiles: FileUpload[] = [];
      const errors: string[] = [];
      
      // Verificar limite de arquivos
      if (files.length + fileArray.length > MAX_FILES) {
        errors.push(`Máximo de ${MAX_FILES} arquivos permitidos`);
        setUploadError(errors.join("; "));
        setTimeout(() => setUploadError(null), 5000);
        return;
      }
      
      fileArray.forEach((file) => {
        // Verificar se o arquivo já existe (nome + tamanho)
        const exists = files.some((f) => f.file.name === file.name && f.file.size === file.size);
        if (exists) {
          errors.push(`Arquivo "${file.name}" já foi adicionado`);
          return;
        }
        
        // Validar o arquivo
        const error = validateFile(file);
        if (error) {
          errors.push(`${file.name}: ${error}`);     
          return;
        }
        
        // Adicionar à lista de arquivos válidos
        validFiles.push({
          id: `${Date.now()}-${Math.random()}-${file.name}`,
          file,
          progress: 0,
          status: "pending",
        });
      });
      
      if (errors.length > 0) {
        setUploadError(errors.join("; "));
        setTimeout(() => setUploadError(null), 5000);
      }
      
      if (validFiles.length > 0) {
        setFiles((prev) => [...prev, ...validFiles]);
      }
    },
    [files], // Manter dependência de files para funcionar corretamente
  );

  const removeFile = useCallback((id: string) => {
    setFiles((prev) => prev.filter((f) => f.id !== id));
  }, []);

  const handleDrop = useCallback(
    (e: React.DragEvent) => {
      e.preventDefault();
      setIsDragging(false);
      const droppedFiles = e.dataTransfer.files;
      if (droppedFiles && droppedFiles.length > 0) {
        addFiles(droppedFiles);
      }
    },
    [addFiles],
  );

  const handleDragOver = useCallback((e: React.DragEvent) => {
    e.preventDefault();
    setIsDragging(true);
  }, []);

  const handleDragLeave = useCallback((e: React.DragEvent) => {
    e.preventDefault();
    setIsDragging(false);
  }, []);

  const handleFileSelect = () => {
    fileInputRef.current?.click();
  };

  const handleFileChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    const selectedFiles = e.target.files;
    if (selectedFiles && selectedFiles.length > 0) {
      addFiles(selectedFiles);
    }
    // Limpar o input para evitar problemas de duplicação
    if (fileInputRef.current) {
      fileInputRef.current.value = "";
    }
  };

  const firstFileExtension = (): string | undefined => {
    const name = files[0]?.file.name;
    if (!name) return undefined;
    const m = name.toLowerCase().match(/(\.[a-z0-9]+)$/);
    return m ? m[1] : undefined;
  };

  const runUpload = async (wf: WorkflowSuggestionApplyResult | null) => {
    setIsUploading(true);
    setUploadError(null);
    abortControllerRef.current = new AbortController();
    setFiles((prev) => prev.map((f) => ({ ...f, status: "uploading" as const })));
    try {
      const workflow: CreateDocumentWorkflowOptions | undefined =
        customerId && wf ? toUploadWorkflowOptions(wf, firstFileExtension()) : undefined;
      const response = await documentService.uploadDocuments({
        files: files.map((f) => f.file),
        path: selectedPath || '/',
        workflow,
      });
      setFiles((prev) => prev.map((f) => ({ ...f, progress: 100, status: "completed" as const })));
      onDocumentUploaded?.(response);
      setTimeout(() => {
        handleClose();
      }, 1000);
    } catch (error) {
      setFiles((prev) => prev.map((f) => ({ ...f, status: "error" as const, error: "Erro no upload" })));
      setUploadError(`Erro durante o upload dos arquivos: ${error instanceof Error ? error.message : 'Erro desconhecido'}`);
    } finally {
      setIsUploading(false);
    }
  };

  const uploadFiles = async () => {
    if (files.length === 0) return;
    if (customerId) {
      setShowWorkflowModal(true);
      return;
    }
    await runUpload(null);
  };

  const handleWorkflowConfirm = async (result: WorkflowSuggestionApplyResult) => {
    setShowWorkflowModal(false);
    await runUpload(result);
  };

  const cancelUpload = () => {
    abortControllerRef.current?.abort();
    setIsUploading(false);
    setFiles((prev) => prev.map((f) => (f.status === "uploading" ? { ...f, status: "pending", progress: 0 } : f)));
  };

  const handleClose = () => {
    if (isUploading) {
      cancelUpload();
    }
    setFiles([]);
    setUploadError(null);
    // Limpar o input de arquivo também
    if (fileInputRef.current) {
      fileInputRef.current.value = "";
    }
    onClose();
  };

  const getFileIcon = (file: File) => {
    if (file.type.startsWith("image/")) return "🖼️";
    if (file.type.includes("pdf")) return "📄";
    if (file.type.includes("word")) return "📝";
    if (file.type.includes("excel") || file.type.includes("sheet")) return "📊";
    if (file.type.includes("zip") || file.type.includes("compressed") || file.type.includes("rar") || file.type.includes("7z") || file.type.includes("tar") || file.type.includes("gzip") || file.type.includes("bzip2")) return "🗜️";
    return "📄";
  };

  const formatFileSize = (bytes: number) => {
    if (bytes === 0) return "0 Bytes";
    const k = 1024;
    const sizes = ["Bytes", "KB", "MB", "GB"];
    const i = Math.floor(Math.log(bytes) / Math.log(k));
    return Number.parseFloat((bytes / Math.pow(k, i)).toFixed(2)) + " " + sizes[i];
  };

  const completedFiles = files.filter((f) => f.status === "completed").length;
  const totalFiles = files.length;
  const hasErrors = files.some((f) => f.status === "error");

  const clearAllFiles = useCallback(() => {
    setFiles([]);
    setUploadError(null);
    // Limpar o input de arquivo também
    if (fileInputRef.current) {
      fileInputRef.current.value = "";
    }
  }, []);

  return (
    <>
    <WorkflowSuggestionModal
      isOpen={showWorkflowModal}
      onClose={() => setShowWorkflowModal(false)}
      customerId={customerId || ''}
      fileExtension={firstFileExtension()}
      previewPdfFile={
        files.find(
          (f) =>
            f.file.type.includes('pdf') ||
            f.file.name.toLowerCase().endsWith('.pdf')
        )?.file ?? null
      }
      previewPdfFiles={files
        .map((f) => f.file)
        .filter(
          (file) =>
            file.type.includes('pdf') ||
            file.name.toLowerCase().endsWith('.pdf')
        )}
      folderId={folderDocumentId}
      onConfirm={handleWorkflowConfirm}
    />
    <Dialog 
      open={isOpen} 
      onClose={() => {
        if (showWorkflowModal) return;
        handleClose();
      }} 
      className="fixed inset-0 z-50 overflow-y-auto"
      as="div"
    >
      <div className="flex items-center justify-center min-h-screen">
        <Dialog.Overlay 
          className="fixed inset-0 bg-black opacity-30" 
          aria-hidden="true"
        />
        
        <div className="relative bg-white rounded-lg max-w-2xl w-full mx-4 p-6">
          <Dialog.Title as="h2" className="text-lg font-medium mb-4">
            Upload de Arquivos
          </Dialog.Title>
          <div
            className={`border-2 border-dashed rounded-lg p-8 text-center transition-all duration-200 ${
              isDragging ? "border-blue-500 bg-blue-50 scale-105" : "border-gray-300 hover:border-gray-400 hover:bg-gray-50"
            }`}
            onDrop={handleDrop}
            onDragOver={handleDragOver}
            onDragLeave={handleDragLeave}
            role="button"
            tabIndex={0}
            aria-label="Área para arrastar e soltar arquivos"
            onKeyDown={(e) => {
              if (e.key === 'Enter' || e.key === ' ') {
                e.preventDefault();
                handleFileSelect();
              }
            }}
          >
            <span className="text-4xl animate-bounce">⬆️</span>
            <p className="text-lg font-medium text-gray-900 mb-2">Arraste e solte seus arquivos aqui</p>
            <p className="text-sm text-gray-500 mb-4">ou clique para selecionar arquivos</p>
            <button
              type="button"
              className="px-4 py-2 border rounded bg-gray-100 hover:bg-gray-200 disabled:opacity-50 disabled:cursor-not-allowed transition-all duration-200 hover:shadow-md"
              onClick={handleFileSelect}
              disabled={isUploading}
              aria-label="Selecionar arquivos para upload"
            >
              Selecionar Arquivos
            </button>
            <input
              ref={fileInputRef}
              type="file"
              multiple
              onChange={handleFileChange}
              className="hidden"
              accept=".pdf,.doc,.docx,.xls,.xlsx,.txt,.jpg,.jpeg,.png,.gif,.zip,.rar,.7z,.tar,.gz,.bz2,.csv,.html"
              aria-label="Selecionar arquivos para upload"
            />
            <p className="text-xs text-gray-400 mt-2">
              Máximo {MAX_FILES} arquivos, {MAX_FILE_SIZE / 1024 / 1024}MB cada
            </p>
          </div>

          {/* Lista de Arquivos */}
          {files.length > 0 && (
            <div className="space-y-2 mt-4">
              <div className="flex items-center justify-between">
                <h3 className="font-medium">
                  Arquivos ({files.length}/{MAX_FILES})
                </h3>
                {!isUploading && (
                  <button 
                    type="button"
                    className="text-blue-600 text-sm hover:text-blue-800 focus:outline-none focus:ring-2 focus:ring-blue-500 rounded transition-colors hover:bg-blue-50 px-2 py-1"
                    onClick={clearAllFiles}
                    aria-label="Limpar todos os arquivos"
                  >
                    Limpar Todos
                  </button>
                )}
              </div>
              <div className="max-h-60 overflow-y-auto space-y-2">
                {files.map((fileUpload) => (
                  <div 
                    key={fileUpload.id} 
                    className="flex items-center gap-3 p-3 border rounded-lg"
                    role="listitem"
                    aria-label={`Arquivo: ${fileUpload.file.name}`}
                  >
                    <div className="text-2xl">{getFileIcon(fileUpload.file)}</div>
                    <div className="flex-1 min-w-0">
                      <div className="flex items-center gap-2">
                        <p className="text-sm font-medium truncate">{fileUpload.file.name}</p>
                        <span className="text-xs text-gray-500">{formatFileSize(fileUpload.file.size)}</span>
                      </div>
                      {fileUpload.status === "uploading" && (
                        <div className="mt-2">
                          <div className="w-full bg-gray-200 rounded h-2" role="progressbar" aria-label={`Progresso do upload de ${fileUpload.file.name}`}>
                            <div
                              className="bg-blue-500 h-2 rounded transition-all duration-300"
                              style={{ width: `${fileUpload.progress}%` }}
                            />
                          </div>
                          <p className="text-xs text-gray-500 mt-1">{Math.round(fileUpload.progress)}%</p>
                        </div>
                      )}
                      {fileUpload.status === "error" && (
                        <p className="text-xs text-red-500 mt-1">{fileUpload.error}</p>
                      )}
                    </div>
                    <div className="flex items-center gap-2">
                      {fileUpload.status === "completed" && <span className="text-green-500 animate-pulse">✔️</span>}
                      {fileUpload.status === "error" && <span className="text-red-500 animate-pulse">❌</span>}
                      {fileUpload.status === "uploading" && (
                        <span className="animate-spin text-blue-500">🔄</span>
                      )}
                      {!isUploading && fileUpload.status !== "completed" && (
                        <button 
                          type="button"
                          className="text-red-500 hover:text-red-700 focus:outline-none focus:ring-2 focus:ring-red-500 rounded transition-colors"
                          onClick={() => removeFile(fileUpload.id)}
                          aria-label={`Remover arquivo ${fileUpload.file.name}`}
                        >
                          <span className="hover:scale-110 transition-transform">✖️</span>
                        </button>
                      )}
                    </div>
                  </div>
                ))}
              </div>
            </div>
          )}

          {/* Progresso Geral */}
          {isUploading && (
            <div className="space-y-2 mt-4">
              <div className="flex items-center justify-between">
                <span className="text-sm font-medium">
                  Enviando arquivos... ({completedFiles}/{totalFiles})
                </span>
                <button 
                  type="button"
                  className="text-red-500 text-sm hover:text-red-700 focus:outline-none focus:ring-2 focus:ring-red-500 rounded transition-colors hover:bg-red-50 px-2 py-1"
                  onClick={cancelUpload}
                  aria-label="Cancelar upload"
                >
                  Cancelar
                </button>
              </div>
              <div className="w-full bg-gray-200 rounded h-2" role="progressbar" aria-label="Progresso geral do upload">
                <div
                  className="bg-blue-500 h-2 rounded transition-all duration-300"
                  style={{ width: `${(completedFiles / totalFiles) * 100}%` }}
                />
              </div>
            </div>
          )}

          {/* Alertas */}
          {uploadError && (
            <div className="bg-red-100 text-red-700 p-2 rounded mt-2 text-sm" role="alert" aria-live="polite">
              {uploadError}
            </div>
          )}
          {hasErrors && !isUploading && (
            <div className="bg-red-100 text-red-700 p-2 rounded mt-2 text-sm" role="alert" aria-live="polite">
              Alguns arquivos falharam no upload. Remova-os e tente novamente.
            </div>
          )}
          {completedFiles === totalFiles && totalFiles > 0 && !isUploading && (
            <div className="bg-green-100 text-green-700 p-2 rounded mt-2 text-sm" role="alert" aria-live="polite">
              Todos os arquivos foram carregados com sucesso!
            </div>
          )}

          {/* Botões de Ação */}
          <div className="flex justify-end gap-3 pt-4 border-t mt-4">
            <button
              type="button"
              className="px-4 py-2 border rounded bg-gray-100 hover:bg-gray-200 disabled:opacity-50 disabled:cursor-not-allowed transition-all duration-200 hover:shadow-md"
              onClick={handleClose}
              disabled={isUploading}
            >
              {isUploading ? "Cancelar" : "Fechar"}
            </button>
            <button
              type="button"
              className="px-4 py-2 bg-blue-600 text-white rounded hover:bg-blue-700 disabled:opacity-50 disabled:cursor-not-allowed transition-all duration-200 hover:shadow-md"
              onClick={uploadFiles}
              disabled={files.length === 0 || isUploading || hasErrors}
            >
              {isUploading ? "Enviando..." : `Enviar${files.length > 0 ? ` (${files.length})` : ""}`}
            </button>
          </div>
        </div>
      </div>
    </Dialog>
    </>
  );
};